import { useState } from "react";
import { MemberFilePicker } from "../shared/MemberFilePicker";
import { Alert, Button, Field } from "../shared/ui";
import { downloadMemberFile, saveBlob } from "../shared/api";
import type { TransactionDocument } from "../shared/types";
import { evidenceTypeName, updateEvidenceFiles, type EvidenceSelection, type TransactionEvidenceGuide } from "./transaction-evidence";
import "./TransactionEvidence.css";

export function TransactionEvidenceGuidance({ guide }: { readonly guide: TransactionEvidenceGuide }) {
  return <section className="transaction-evidence-guide" aria-label={`${guide.categoryName} 증빙 안내`}>
    <h3>{guide.categoryName} 증빙 안내</h3>
    <p className="transaction-evidence-guide__recommendation">{guide.recommendedDocumentNames.join(", ")}</p>
    <p>{guide.alternativeEvidenceDescription}</p>
  </section>;
}

export function TransactionEvidencePicker({ guide, selection, onChange, label = "증빙파일 첨부", presentation }: {
  readonly guide: TransactionEvidenceGuide;
  readonly selection: readonly EvidenceSelection[];
  readonly onChange: (selection: readonly EvidenceSelection[]) => void;
  readonly label?: string;
  readonly presentation?: "registration";
}) {
  const [error, setError] = useState<string>();
  const unsupportedType = selection.some(item => !guide.documentTypeOptions.some(option => option.code === item.documentTypeCode));
  return <div className="transaction-evidence">
    <TransactionEvidenceGuidance guide={guide} />
    {error && <Alert persistent variant="warning" title="첨부파일 수를 확인해 주세요">{error}</Alert>}
    {unsupportedType && <Alert persistent variant="warning" title="서류 종류를 확인해 주세요">납부 종류에 맞는 서류 종류를 각 파일에서 선택해 주세요. 기존 파일의 분류를 임의로 바꾸지 않습니다.</Alert>}
    <MemberFilePicker label={label} files={selection.map(item => item.file)} maxFiles={guide.maximumDocumentCount} required rejectOverflow appendFiles
      presentation={presentation === "registration" ? "evidence" : undefined}
      triggerDescription={presentation === "registration" ? `${selection.length}/${guide.maximumDocumentCount}장 첨부됨` : undefined}
      onChange={files => {
        if (files.length > guide.maximumDocumentCount) { setError(`증빙은 최대 ${guide.maximumDocumentCount}개까지 첨부할 수 있습니다. 선택한 파일을 줄여 주세요.`); return; }
        setError(undefined); onChange(updateEvidenceFiles(selection, files, guide));
      }}
      renderFileDetails={(file, index) => <Field label="서류 종류" aria-label={`${file.name} 서류 종류`} control="select" required value={guide.documentTypeOptions.some(option => option.code === selection[index]?.documentTypeCode) ? selection[index]?.documentTypeCode : ""}
        description="파일 내용에 맞는 종류를 선택해 주세요. 그 밖의 자료는 대체 증빙으로 제출합니다."
        onChange={event => onChange(selection.map((item, at) => at === index ? { ...item, documentTypeCode: event.currentTarget.value } : item))}>
        <option value="">종류를 확인해 주세요</option>{guide.documentTypeOptions.map(option => <option value={option.code} key={option.code}>{option.name}</option>)}
      </Field>} />
    <p className="field-note">{presentation === "registration" ? "" : `첨부 ${selection.length}/${guide.maximumDocumentCount}개 · `}파일당 최대 20MB</p>
    <p className="field-note">최종 등록 시 업로드·검사를 진행합니다. 안내된 서류를 모두 첨부할 필요는 없습니다.</p>
  </div>;
}

export function TransactionEvidenceSummary({ selection, guide }: { readonly selection: readonly EvidenceSelection[]; readonly guide: TransactionEvidenceGuide }) {
  return <ul className="transaction-evidence-summary">{selection.map((item, index) => <li key={`${item.file.name}-${index}`}><strong>{item.file.name}</strong><span>{evidenceTypeName(item.documentTypeCode, guide)}</span></li>)}</ul>;
}

export function ExistingTransactionEvidence({ documents }: { readonly documents: readonly TransactionDocument[] }) {
  const [error, setError] = useState<string>();
  const [downloading, setDownloading] = useState<number>();
  const active = documents.filter(document => document.active === true);
  const previous = documents.filter(document => document.active !== true);
  const records = (items: readonly TransactionDocument[]) => <ul className="transaction-existing-documents">{items.map(document => <li key={document.id}>
    <strong>{document.originalFilename ?? `첨부자료 ${document.id}`}</strong>
    <span>{document.documentTypeName || "종류 확인 필요"} · {document.active ? "현재 제출 자료" : "이전 제출 자료"}</span>
    {document.downloadable ? <Button size="small" variant="secondary" isLoading={downloading === document.id}
      onClick={async () => { setError(undefined); setDownloading(document.id); try { saveBlob(await downloadMemberFile(document.fileAssetId), document.originalFilename ?? `transaction-document-${document.id}`); } catch (cause) { setError(cause instanceof Error ? cause.message : "파일을 내려받지 못했습니다. 다시 확인해 주세요."); } finally { setDownloading(undefined); } }}>파일 다운로드</Button>
      : <span>현재 이 파일을 내려받을 수 없습니다.</span>}
  </li>)}</ul>;
  return <section className="transaction-existing-evidence" aria-label="제출한 증빙 자료">
    {error && <Alert persistent variant="danger" title="파일을 확인하지 못했습니다">{error}</Alert>}
    <h3>현재 제출 자료 · {active.length}개</h3>
    {active.length > 0 ? records(active) : <p className="field-note">현재 유효한 제출 자료가 없습니다.</p>}
    {previous.length > 0 && <><h3>이전 제출 자료 · {previous.length}개</h3><p className="field-note">이전 자료는 이력으로 보관하며 현재 심사 첨부로 계산하지 않습니다.</p>{records(previous)}</>}
  </section>;
}
